import * as React from 'react'
import type { JSONContent } from '@tiptap/core'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { DetailPage } from '@/components/templates/detail-page'
import { ListPage, type ListPageColumn } from '@/components/templates/list-page'
import { Alert } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  BlastRichTextEditor,
  type BlastMergeFieldOption,
} from '@/components/ui/blast-rich-text-editor'
import { deleteJSON, getJSON, postJSON } from '@/lib/api'
import {
  BlastAudienceRail,
  AudienceExclusions,
  consentEvidenceWarning,
  type AudienceEstimate,
  useBlastAudience,
} from '@/pages/BlastAudienceRail'
import { blastDeliveryPageState, BlastDeliveryGateNotice } from '@/pages/BlastDeliveryGateNotice'
import type {
  Blast,
  BlastDeliveryGate,
  EmailIdentitySettingsResponse,
} from '@/pages/BlastsPage.types'
import { BlastDeletionDialog } from '@/pages/BlastDeletionDialog'
import { BrandHeader, SendTimeFurniture } from '@/pages/BlastComposerFurniture'
import { CancelScheduleDialog, ScheduleDialog } from '@/pages/BlastScheduleDialogs'
import { blastComposerStatus, useBlastDraftAutosave } from '@/pages/useBlastDraftAutosave'
import { firstError, useBlastDuplicate } from '@/pages/useBlastDuplicate'
import { useViewOnly } from '@/components/templates/app-shell'

export function BlastsPage({ onOpenReport }: { onOpenReport?: (blastId: number) => void }) {
  const viewOnly = useViewOnly()
  const queryClient = useQueryClient()
  const [composerOpen, setComposerOpen] = React.useState(false)
  const [editingBlast, setEditingBlast] = React.useState<Blast | null>(null)
  const [confirmBlast, setConfirmBlast] = React.useState<Blast | null>(null)
  const [scheduleBlast, setScheduleBlast] = React.useState<Blast | null>(null)
  const [cancelBlast, setCancelBlast] = React.useState<Blast | null>(null)
  const [deleteBlast, setDeleteBlast] = React.useState<Blast | null>(null)
  const query = useQuery({
    queryKey: ['blasts'],
    queryFn: () => getJSON<{ blasts: Blast[]; delivery_gate: BlastDeliveryGate }>('/api/v1/blasts'),
    retry: false,
  })
  const send = useMutation({
    mutationFn: (id: number) => postJSON(`/api/v1/blasts/${id}/send_now`, {}),
    onSuccess: () => {
      setConfirmBlast(null)
      return queryClient.invalidateQueries({ queryKey: ['blasts'] })
    },
  })
  const cancel = useMutation({
    mutationFn: (id: number) => postJSON(`/api/v1/blasts/${id}/cancel`, {}),
    onSuccess: () => {
      setCancelBlast(null)
      return queryClient.invalidateQueries({ queryKey: ['blasts'] })
    },
  })
  const remove = useMutation({
    mutationFn: (id: number) => deleteJSON(`/api/v1/blasts/${id}`),
    onSuccess: () => {
      setDeleteBlast(null)
      return queryClient.invalidateQueries({ queryKey: ['blasts'] })
    },
  })
  const duplicate = useBlastDuplicate<Blast>((blast) => {
    setEditingBlast(blast)
    setComposerOpen(true)
  })
  const pageState = blastDeliveryPageState(
    query.data,
    firstError(send.error, cancel.error, remove.error, duplicate.error),
  )

  if (composerOpen && !viewOnly)
    return <BlastComposer blast={editingBlast} open={composerOpen} onOpenChange={setComposerOpen} />

  return (
    <>
      <ListPage
        title="Blasts"
        description="Draft and send announcements from your verified Sending Domain."
        primaryAction={{
          label: 'New Blast',
          onClick: () => {
            setEditingBlast(null)
            setComposerOpen(true)
          },
        }}
        toolbar={<BlastDeliveryGateNotice {...pageState} />}
        table={{
          rows: query.data?.blasts ?? [],
          columns: columns,
          getRowId: (blast) => blast.id,
          getRowLabel: (blast) => blast.subject,
          state: blastTableState(query),
          loadingTitle: 'Loading Blasts',
          errorTitle: 'Blasts unavailable',
          emptyTitle: 'No Blasts yet',
          emptyDescription: 'Create a draft to hand-pick recipients and send an announcement.',
          actions: (blast) =>
            viewOnly
              ? []
              : blastActions(
                  blast,
                  {
                    edit: () => {
                      setEditingBlast(blast)
                      setComposerOpen(true)
                    },
                    send: () => setConfirmBlast(blast),
                    schedule: () => setScheduleBlast(blast),
                    cancel: () => setCancelBlast(blast),
                    delete: () => setDeleteBlast(blast),
                    duplicate: () => duplicate.mutate(blast.id),
                  },
                  pageState.deliveryEnabled,
                ),
          onRowOpen: (blast) => {
            if (['sending', 'paused', 'sent'].includes(blast.status) && onOpenReport)
              onOpenReport(blast.id)
            else if (blast.status === 'canceled' && blast.resolved && onOpenReport)
              onOpenReport(blast.id)
            else if (!viewOnly && (blast.status === 'draft' || blast.status === 'scheduled')) {
              setEditingBlast(blast)
              setComposerOpen(true)
            }
          },
        }}
      />
      <SendConfirmation
        blast={confirmBlast}
        pending={send.isPending}
        onClose={() => setConfirmBlast(null)}
        onSend={(blast) => send.mutate(blast.id)}
      />
      <ScheduleDialog blast={scheduleBlast} onClose={() => setScheduleBlast(null)} />
      <CancelScheduleDialog
        blast={cancelBlast}
        pending={cancel.isPending}
        onClose={() => setCancelBlast(null)}
        onCancel={(blast) => cancel.mutate(blast.id)}
      />
      <BlastDeletionDialog
        blast={deleteBlast}
        pending={remove.isPending}
        onClose={() => setDeleteBlast(null)}
        onDelete={(blast) => remove.mutate(blast.id)}
      />
    </>
  )
}

function BlastComposer({
  blast,
  open,
  onOpenChange,
}: {
  blast: Blast | null
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const queryClient = useQueryClient()
  const [subject, setSubject] = React.useState('')
  const [bodyDocument, setBodyDocument] = React.useState<JSONContent>(emptyDocument())
  const audience = useBlastAudience(open)
  const settings = useQuery({
    queryKey: ['email-identity-settings', 'blast-composer'],
    queryFn: () => getJSON<EmailIdentitySettingsResponse>('/api/v1/email_identity_settings'),
    retry: false,
  })
  const mergeFields = useQuery({
    queryKey: ['blast-merge-fields'],
    queryFn: () =>
      getJSON<{ merge_fields: BlastMergeFieldOption[] }>('/api/v1/blasts/merge_fields'),
    retry: false,
  })
  React.useEffect(() => {
    if (!open) return
    setSubject(blast?.subject ?? '')
    setBodyDocument(blast?.body_document ?? emptyDocument())
    if (blast?.audience) audience.load(blast.audience)
    else audience.reset()
    // Audience setters are stable for the lifetime of this mounted composer.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [blast, open])
  const create = useMutation({
    mutationFn: () =>
      postJSON('/api/v1/blasts', {
        blast: { subject, body_document: bodyDocument, ...audience.payload },
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['blasts'] })
      setSubject('')
      setBodyDocument(emptyDocument())
      audience.reset()
      onOpenChange(false)
    },
  })
  const autosave = useBlastDraftAutosave({
    blast,
    subject,
    bodyDocument,
    audience: audience.payload,
  })

  const branding = settings.data?.email_identity_settings

  return (
    <DetailPage
      title={subject || 'New Blast'}
      badge={
        <Badge variant="neutral">{blast?.status === 'scheduled' ? 'Scheduled' : 'Draft'}</Badge>
      }
      subtitle={blastComposerStatus(Boolean(blast), autosave)}
      backHref="/communication/blasts"
      backLabel="Blasts"
      onBack={() => (blast ? autosave.flushThen(() => onOpenChange(false)) : onOpenChange(false))}
      actions={
        blast ? null : (
          <Button type="button" disabled={create.isPending} onClick={() => create.mutate()}>
            Save draft
          </Button>
        )
      }
    >
      {create.error || autosave.error ? (
        <Alert>{create.error?.message ?? autosave.error?.message}</Alert>
      ) : null}
      <div className="grid gap-6 lg:grid-cols-[1fr_340px]">
        <div className="flex flex-col gap-4">
          <div className="max-w-2xl space-y-1.5">
            <Label htmlFor="blast-subject">Subject</Label>
            <Input
              id="blast-subject"
              value={subject}
              onChange={(event) => setSubject(event.target.value)}
            />
          </div>
          <Card className="overflow-hidden">
            <BlastRichTextEditor
              value={bodyDocument}
              onChange={setBodyDocument}
              beforeContent={<BrandHeader senderName={branding?.sender_name} />}
              afterContent={<SendTimeFurniture postalAddress={branding?.postal_address} />}
              mergeFields={mergeFields.data?.merge_fields}
            />
          </Card>
        </div>
        <BlastAudienceRail audience={audience} />
      </div>
    </DetailPage>
  )
}

function emptyDocument(): JSONContent {
  return { type: 'doc', content: [{ type: 'paragraph' }] }
}

function blastTableState(query: { isLoading: boolean; isError: boolean }) {
  if (query.isLoading) return 'loading' as const
  if (query.isError) return 'error' as const
  return 'ready' as const
}

function blastActions(
  blast: Blast,
  actions: {
    edit: () => void
    send: () => void
    schedule: () => void
    cancel: () => void
    delete: () => void
    duplicate: () => void
  },
  deliveryEnabled: boolean,
) {
  if (blast.status === 'draft')
    return [
      { label: 'Edit', onSelect: actions.edit },
      { label: 'Duplicate', onSelect: actions.duplicate },
      { label: 'Schedule', onSelect: actions.schedule, disabled: !deliveryEnabled },
      { label: 'Send now', onSelect: actions.send, disabled: !deliveryEnabled },
      { label: 'Delete', onSelect: actions.delete, destructive: true },
    ]
  if (blast.status === 'scheduled')
    return [
      { label: 'Edit', onSelect: actions.edit },
      { label: 'Duplicate', onSelect: actions.duplicate },
      { label: 'Reschedule', onSelect: actions.schedule },
      { label: 'Cancel schedule', onSelect: actions.cancel },
    ]
  return [{ label: 'Duplicate', onSelect: actions.duplicate }]
}

function SendConfirmation({
  blast,
  pending,
  onClose,
  onSend,
}: {
  blast: Blast | null
  pending: boolean
  onClose: () => void
  onSend: (blast: Blast) => void
}) {
  const estimateQuery = useQuery({
    queryKey: ['blast-confirm-estimate', blast?.id],
    queryFn: () =>
      postJSON<{ estimate: AudienceEstimate }>('/api/v1/blasts/estimate', {
        blast: {
          segment_ids: blast?.audience?.segments.map((option) => option.id) ?? [],
          group_ids: blast?.audience?.groups.map((option) => option.id) ?? [],
          contact_ids: blast?.audience?.contacts.map((option) => option.id) ?? [],
        },
      }),
    enabled: blast !== null,
    retry: false,
  })
  const estimate = estimateQuery.data?.estimate ??
    blast?.estimate ?? {
      recipients: 0,
      excluded: 0,
      no_consent_basis: 0,
    }
  return (
    <Dialog open={blast !== null} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-md gap-2">
        <DialogHeader>
          <DialogTitle>Send &quot;{blast?.subject}&quot;?</DialogTitle>
        </DialogHeader>
        <div className="space-y-2">
          <div className="flex items-baseline gap-2">
            <span className="text-3xl font-normal tracking-tight text-foreground">
              ~{estimate.recipients}
            </span>
            <span className="text-body-sm text-muted-foreground">
              recipients · resolved at send
            </span>
          </div>
          <p className="text-caption text-muted-foreground">
            <span className="text-warning">{estimate.excluded}</span> excluded
          </p>
          <AudienceExclusions exclusions={estimate.exclusions} />
          {estimate.no_consent_basis > 0 ? (
            <Alert variant="warning">{consentEvidenceWarning(estimate.no_consent_basis)}</Alert>
          ) : null}
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>
            Back
          </Button>
          <Button type="button" disabled={!blast || pending} onClick={() => blast && onSend(blast)}>
            Send to ~{estimate.recipients} people
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

const columns: ListPageColumn<Blast>[] = [
  { id: 'subject', header: 'Subject', cell: (blast) => blast.subject },
  {
    id: 'status',
    header: 'Status',
    cell: (blast) => (
      <Badge variant={blast.status === 'sent' ? 'success' : 'neutral'}>{blast.status}</Badge>
    ),
  },
  {
    id: 'recipients',
    header: 'Recipients',
    cell: (blast) => recipientSummary(blast),
  },
]

function recipientSummary(blast: Blast) {
  if (blast.status === 'draft' || blast.status === 'scheduled') {
    const estimate = blast.estimate ?? { recipients: 0, excluded: 0, no_consent_basis: 0 }
    const timing = blast.scheduled_for_label ? ` · scheduled ${blast.scheduled_for_label}` : ''
    return `~${estimate.recipients} estimated · ${estimate.excluded} excluded${timing}`
  }
  if (blast.status === 'canceled' && !blast.resolved) return 'Canceled before sending'
  const estimate = blast.estimate ?? { recipients: 0, excluded: 0, no_consent_basis: 0 }
  const resolved = blast.resolved ?? {
    recipients: blast.sent_count,
    excluded: 0,
    no_consent_basis: 0,
  }
  const excluded = blast.recipients.filter((recipient) => recipient.status === 'excluded')
  const counts = `~${estimate.recipients} estimated · ${resolved.recipients} sent · ${resolved.excluded} excluded`
  if (excluded.length === 0) return counts
  return `${counts}: ${excluded
    .map((recipient) => `${recipient.contact_name} (${recipient.exclusion_reason})`)
    .join(', ')}`
}
